// client/src/components/pages/CustomSummaries/index.js
import React, { useState, useEffect } from 'react';
import {
  FaPlus,
  FaSearch,
  FaFilter,
  FaPlay,
  FaEdit,
  FaCopy,
  FaTrash,
  FaShare,
  FaClock,
  FaChartBar,
  FaTable,
  FaDownload,
  FaStar,
  FaRegStar,
  FaDatabase,
} from 'react-icons/fa';
import api from '../../../services/api';
import SummaryCard from './SummaryCard';
import CreateSummaryModal from './CreateSummaryModal';
import ExecuteSummaryModal from './ExecuteSummaryModal';
import ShareSummaryModal from './ShareSummaryModal';
import TemplateLibraryModal from './TemplateLibraryModal';
import FilterPanel from './FilterPanel';
import LoadingSpinner from '../../common/LoadingSpinner';
import LexiBridgeLoader from '../../common/LexiBridgeLoader';
import Pagination from '../../common/Pagination';
import ThemedSelect from '../../common/ThemedSelect';
import PageHeader from '../../common/PageHeader';

const CustomSummaries = () => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSummary, setSelectedSummary] = useState(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Filter and pagination states
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    tags: [],
    createdBy: '',
    isPublic: '',
    isFavorite: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    pages: 0,
  });
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // View mode
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  // Fetch summaries
  const fetchSummaries = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit: pagination.limit,
        sortBy,
        sortOrder,
        ...filters,
      };

      // Remove empty filters
      Object.keys(params).forEach((key) => {
        if (params[key] === '' || (Array.isArray(params[key]) && params[key].length === 0)) {
          delete params[key];
        }
      });

      const response = await api.customSummaries.getAll(params);

      setSummaries(response.data.summaries);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Error fetching summaries:', err);
      setError(err.response?.data?.message || 'Failed to load summaries');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchSummaries();
  }, [filters, sortBy, sortOrder]);

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
    fetchSummaries(page);
  };

  // Handle summary actions
  const handleExecute = async (summary) => {
    try {
      // Fetch full summary details (including SQLQuery, QueryParameters) for execution
      const response = await api.customSummaries.get(summary.SummaryID);
      setSelectedSummary(response.data);
      setShowExecuteModal(true);
    } catch (err) {
      console.error('Error fetching summary for execution:', err);
      setError(
        err.response?.data?.error || err.response?.data?.message || 'Failed to load summary details'
      );
    }
  };

  const handleEdit = async (summary) => {
    try {
      // Fetch full summary details (including SQLQuery, QueryParameters, ChartConfig)
      const response = await api.customSummaries.get(summary.SummaryID);
      setSelectedSummary(response.data);
      setShowCreateModal(true);
    } catch (err) {
      console.error('Error fetching summary details:', err);
      setError(err.response?.data?.message || 'Failed to load summary details');
    }
  };

  const handleDuplicate = async (summary) => {
    try {
      const newTitle = `${summary.Title} (Copy)`;
      await api.customSummaries.duplicate(summary.SummaryID, newTitle);
      fetchSummaries(pagination.page);
    } catch (err) {
      console.error('Error duplicating summary:', err);
      setError(err.response?.data?.message || 'Failed to duplicate summary');
    }
  };

  const handleDelete = async (summary) => {
    if (!window.confirm(`Are you sure you want to delete "${summary.Title}"?`)) {
      return;
    }

    try {
      await api.customSummaries.delete(summary.SummaryID);
      fetchSummaries(pagination.page);
    } catch (err) {
      console.error('Error deleting summary:', err);
      setError(err.response?.data?.message || 'Failed to delete summary');
    }
  };

  const handleShare = (summary) => {
    setSelectedSummary(summary);
    setShowShareModal(true);
  };

  const handleToggleFavorite = async (summary) => {
    try {
      await api.customSummaries.update(summary.SummaryID, {
        ...summary,
        IsFavorite: !summary.IsFavorite,
      });
      fetchSummaries(pagination.page);
    } catch (err) {
      console.error('Error updating favorite status:', err);
      setError(err.response?.data?.message || 'Failed to update favorite status');
    }
  };

  // Handle modal closes
  const handleModalClose = () => {
    setSelectedSummary(null);
    setShowCreateModal(false);
    setShowExecuteModal(false);
    setShowShareModal(false);
    setShowTemplateModal(false);
  };

  const handleSummaryCreated = () => {
    handleModalClose();
    fetchSummaries(pagination.page);
  };

  const handleSummaryExecuted = () => {
    // Don't close modal - let user see the results first
    fetchSummaries(pagination.page);
  };

  if (loading && summaries.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-gray-50">
        <LexiBridgeLoader message="Loading summaries" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        icon={FaDatabase}
        title="Custom Summaries"
        subtitle="Create, manage, and execute personalized data summaries"
        actions={
          <>
            <button
              type="button"
              onClick={() => setShowTemplateModal(true)}
              aria-label="Templates"
              title="Templates"
              className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 border border-slate-300 bg-surface text-slate-700 hover:border-slate-400 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <FaChartBar className="w-4 h-4 text-slate-500" aria-hidden="true" />
              <span className="page-header__label">Templates</span>
            </button>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center justify-center gap-2 h-10 px-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            >
              <FaPlus className="w-3.5 h-3.5" aria-hidden="true" />
              <span>
                Create<span className="page-header__label"> Summary</span>
              </span>
            </button>
          </>
        }
      />

      <div className="max-w-[1800px] mx-auto p-6">
        {/* Search and Filters */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search summaries..."
                value={filters.search}
                onChange={(e) => handleFilterChange({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Controls */}
            <div className="flex items-center space-x-3">
              {/* View Mode Toggle */}
              <div className="flex items-center bg-surface border border-gray-300 rounded-lg">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-400'}`}
                >
                  <div className="grid grid-cols-2 gap-1 w-4 h-4">
                    <div className="bg-current rounded-sm"></div>
                    <div className="bg-current rounded-sm"></div>
                    <div className="bg-current rounded-sm"></div>
                    <div className="bg-current rounded-sm"></div>
                  </div>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-400'}`}
                >
                  <FaTable />
                </button>
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center px-3 py-2 border rounded-lg transition-colors duration-200 ${
                  showFilters
                    ? 'bg-primary-100 border-primary-300 text-primary-700'
                    : 'bg-surface border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FaFilter className="mr-2" />
                Filters
              </button>

              {/* Sort */}
              <ThemedSelect
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-surface focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="updatedAt-desc">Recently Updated</option>
                <option value="createdAt-desc">Recently Created</option>
                <option value="title-asc">Name A-Z</option>
                <option value="title-desc">Name Z-A</option>
                <option value="lastExecuted-desc">Recently Executed</option>
                <option value="executionCount-desc">Most Used</option>
              </ThemedSelect>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClose={() => setShowFilters(false)}
              />
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-red-600 hover:text-red-800 underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Summary Count */}
        <div className="mb-4 text-sm text-gray-600">
          Showing {summaries.length} of {pagination.total} summaries
        </div>

        {/* Summaries Grid/List */}
        {summaries.length === 0 ? (
          <div className="text-center py-12">
            <FaChartBar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No summaries found</h3>
            <p className="text-gray-600 mb-4">
              {filters.search || filters.category
                ? 'Try adjusting your filters or search terms.'
                : 'Get started by creating your first custom summary.'}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors duration-200"
            >
              <FaPlus className="mr-2" />
              Create Summary
            </button>
          </div>
        ) : (
          <>
            <div
              className={
                viewMode === 'grid'
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                  : 'space-y-4'
              }
            >
              {summaries.map((summary) => (
                <SummaryCard
                  key={summary.SummaryID}
                  summary={summary}
                  viewMode={viewMode}
                  onExecute={() => handleExecute(summary)}
                  onEdit={() => handleEdit(summary)}
                  onDuplicate={() => handleDuplicate(summary)}
                  onDelete={() => handleDelete(summary)}
                  onShare={() => handleShare(summary)}
                  onToggleFavorite={() => handleToggleFavorite(summary)}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="mt-8">
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.pages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}

        {/* Loading Overlay */}
        {loading && summaries.length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
            <div className="bg-surface rounded-lg p-6">
              <LoadingSpinner size="md" />
              <p className="mt-2 text-gray-600">Loading summaries...</p>
            </div>
          </div>
        )}

        {/* Modals */}
        {showCreateModal && (
          <CreateSummaryModal
            summary={selectedSummary}
            onClose={handleModalClose}
            onSummaryCreated={handleSummaryCreated}
          />
        )}

        {showExecuteModal && selectedSummary && (
          <ExecuteSummaryModal
            summary={selectedSummary}
            onClose={handleModalClose}
            onExecuted={handleSummaryExecuted}
          />
        )}

        {showShareModal && selectedSummary && (
          <ShareSummaryModal summary={selectedSummary} onClose={handleModalClose} />
        )}

        {showTemplateModal && (
          <TemplateLibraryModal
            onClose={handleModalClose}
            onTemplateSelected={(template) => {
              setShowTemplateModal(false);
              setSelectedSummary({
                Title: template.template_name,
                Description: template.description,
                SQLQuery: template.query_template || '',
                QueryParameters: template.parameter_schema?.parameters
                  ? template.parameter_schema.parameters.reduce((acc, p) => {
                      acc[p.name] = {
                        type: p.type,
                        defaultValue: p.default || '',
                        required: p.required,
                      };
                      return acc;
                    }, {})
                  : {},
                VisualizationType: 'table',
                Tags: [],
                Category: '',
                IsPublic: false,
                _fromTemplate: true,
              });
              setShowCreateModal(true);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CustomSummaries;
